import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  BeneficiariesApi,
  Configuration,
  ErrorResponse,
  PaginatedBeneficiariesDto,
} from '@sr-sdks/api-sdk-axios';
import { AxiosError } from 'axios';
import { configuration } from '../../configuration';
import { RootState } from '../../stateStore';
import { ApiLoadingStateEnum } from '../../utils/api/apiLoadingStateEnum';
import axiosInstance from '../../utils/axiosInstance';
import {
  BeneficiarySliceState,
  BeneficiaryWithServiceAccessRole,
} from './beneficiaryState';

const GetBeneficiaryApi = () => {
  return new BeneficiariesApi(
    new Configuration(),
    configuration.SERVICES_API_BASE,
    axiosInstance,
  );
};

const findAll = createAsyncThunk(
  'beneficiary/findAll',
  async (
    {
      beneficiaryTitleId,
      beneficiaryWorkspaceId,
      page,
      pageSize,
      serviceId,
      sortBy,
    }: {
      beneficiaryTitleId?: string;
      beneficiaryWorkspaceId?: string;
      page?: number;
      pageSize?: number;
      serviceId?: string;
      sortBy?: string;
    },
    thunkApi,
  ) => {
    try {
      const result = await GetBeneficiaryApi().beneficiaryControllerFindAll(
        beneficiaryTitleId,
        beneficiaryWorkspaceId,
        serviceId,
        page,
        pageSize,
        sortBy,
      );

      return result.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        (error as AxiosError).response?.data as ErrorResponse,
      );
    }
  },
);

const initialBeneficiaryState: BeneficiarySliceState = {
  beneficiaries: [],
  currentBeneficiary: null,
  isLoading: ApiLoadingStateEnum.idle,
};

const beneficiarySlice = createSlice({
  extraReducers: (builder) => {
    builder
      // FindAll
      .addCase(findAll.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingStateEnum.succeeded;
        const paginatedResponse =
          action.payload as any as PaginatedBeneficiariesDto;

        state.beneficiaries =
          paginatedResponse.items as BeneficiaryWithServiceAccessRole[];
        state.paginationMeta = paginatedResponse.meta;
      })
      .addCase(findAll.pending, (state) => {
        state.isLoading = ApiLoadingStateEnum.loading;
      })
      .addCase(findAll.rejected, (state, action) => {
        state.isLoading = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      });
  },
  initialState: initialBeneficiaryState,
  name: 'beneficiary',
  reducers: {
    reset: () => {
      return {
        ...initialBeneficiaryState,
      };
    },
    // Add new reducer for setting currentBeneficiary
    setCurrentBeneficiary: (
      state,
      action: PayloadAction<BeneficiaryWithServiceAccessRole | null>,
    ) => {
      state.currentBeneficiary = action.payload;
    },
  },
});

const beneficiaryThunk = {
  findAll,
};

const beneficiarySelectors = {
  beneficiaries: (state: RootState) => state.beneficiaries.beneficiaries,
  currentBeneficiary: (state: RootState) =>
    state.beneficiaries.currentBeneficiary,
  errorResponse: (state: RootState) => state.beneficiaries.errorResponse,
  isLoading: (state: RootState) => state.beneficiaries.isLoading,
  paginationMeta: (state: RootState) => state.beneficiaries.paginationMeta,
};

// Update the exported beneficiaryService to make sure the action is available
export const beneficiaryService = {
  ...beneficiaryThunk,
  actions: beneficiarySlice.actions,
  selectors: beneficiarySelectors,
};

export default beneficiarySlice.reducer;
